import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Users, Bell, BellOff, Trash2, UserMinus, Star, Shield, UserPlus, Search, MessageCircle, Edit2, Check, Camera, Loader2, ZoomIn, LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useChat } from '../../contexts/ChatContext';
import { useAuth } from '../../contexts/AuthContext';
import { groupApi, chatApi, avatarApi, getApiBaseUrl } from '../../services/cloudApi';
import AvatarCropModal from '../Common/AvatarCropModal';
import ImageModal from './ImageModal';
import DeleteModal from '../Common/DeleteModal';
import Username, { getDisplayName } from '../Common/Username';
import ContextMenu from '../Common/ContextMenu';
import GroupAvatar from './GroupAvatar';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';
import { getIntlLocale } from '../../utils/dateLocale';

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name[0].toUpperCase();
};

const GroupInfoSidebar = ({ group, isOpen, onClose }) => {
  const { t, i18n } = useTranslation('chat');
  const dateLocaleTag = getIntlLocale(i18n.language);
  const { muteGroup, leaveGroup, deleteGroup, selectGroup, friends, reloadFriends, selectFriend, updateGroup, groupMessages } = useChat();
  const { authToken, user } = useAuth();
  const [groupDetails, setGroupDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [showDestroyConfirm, setShowDestroyConfirm] = useState(false);
  const [showClearMessagesConfirm, setShowClearMessagesConfirm] = useState(false);
  const [showDeleteMessagesConfirm, setShowDeleteMessagesConfirm] = useState(false);
  const [showRemoveMemberConfirm, setShowRemoveMemberConfirm] = useState(null);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [addingMember, setAddingMember] = useState(false);
  const [addMemberError, setAddMemberError] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState(null);
  const [showStarredMessages, setShowStarredMessages] = useState(false);
  const [starredMessages, setStarredMessages] = useState([]);
  const [memberContextMenu, setMemberContextMenu] = useState({ isOpen: false, x: 0, y: 0, member: null });
  const [isEditingName, setIsEditingName] = useState(false);
  const [editingName, setEditingName] = useState('');
  const [updatingName, setUpdatingName] = useState(false);
  const [showGroupCropModal, setShowGroupCropModal] = useState(false);
  const [selectedGroupImageFile, setSelectedGroupImageFile] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [showAllMembers, setShowAllMembers] = useState(false);
  const avatarInputRef = React.useRef(null);

  useEffect(() => {
    if (isOpen && group?.id && authToken) {
      loadGroupDetails();
      if (reloadFriends) reloadFriends();
    }
  }, [isOpen, group?.id, authToken]);

  useEffect(() => {
    if (group && isOpen) {
      const normalized = {
        ...group,
        avatar_url: group.avatar_url || group.avatarUrl,
        avatarUrl: group.avatarUrl || group.avatar_url,
      };
      if (!groupDetails ||
          groupDetails.name !== normalized.name ||
          (groupDetails.avatar_url || groupDetails.avatarUrl) !== (normalized.avatar_url || normalized.avatarUrl)) {
        setGroupDetails(normalized);
      }
    }
  }, [group?.id, group?.name, group?.avatar_url, group?.avatarUrl, isOpen]);

  const loadGroupDetails = async () => {
    if (!group?.id || !authToken) return;
    setIsLoading(true);
    try {
      const result = await groupApi.getGroup({ token: authToken, groupId: group.id });
      if (result?.ok && result?.group) {
        setGroupDetails({
          ...result.group,
          avatar_url: result.group.avatar_url || result.group.avatarUrl || group.avatar_url || group.avatarUrl,
          name: result.group.name || group.name,
        });
      } else {
        setGroupDetails({ ...group, avatar_url: group.avatar_url || group.avatarUrl, name: group.name });
      }
    } catch {
      setGroupDetails(prev => ({ ...(prev || group), avatar_url: prev?.avatar_url || group?.avatar_url || group?.avatarUrl, name: prev?.name || group?.name }));
    } finally {
      setIsLoading(false);
    }
  };

  const loadStarredMessages = async () => {
    if (!group?.id || !authToken) return;
    try {
      const result = await groupApi.getStarredGroupMessages({ token: authToken, groupId: group.id });
      setStarredMessages(result?.ok && result?.messages ? result.messages : []);
    } catch { setStarredMessages([]); }
  };

  const handleToggleMute = async () => {
    if (!group?.id) return;
    await muteGroup(group.id, !group.muted);
    await loadGroupDetails();
  };

  const handleLeaveGroup = async () => {
    if (!group?.id) return;
    await leaveGroup(group.id);
    setShowLeaveConfirm(false);
    onClose();
    selectGroup(null);
  };

  const handleDestroyGroup = async () => {
    if (!group?.id || !authToken) return;
    try {
      const result = await groupApi.destroyGroup({ token: authToken, groupId: group.id });
      if (result?.ok) {
        setShowDestroyConfirm(false);
        await loadGroupDetails();
        window.dispatchEvent(new CustomEvent('notesplus-show-toast', { detail: { type: 'success', title: t('groupInfoSidebar.destroyedSuccessTitle'), message: t('groupInfoSidebar.destroyedSuccessMessage') } }));
      }
    } catch {
      window.dispatchEvent(new CustomEvent('notesplus-show-toast', { detail: { type: 'error', title: t('common:errorTitle'), message: t('groupInfoSidebar.destroyErrorMessage') } }));
    }
  };

  const handleClearMessages = async () => {
    if (!group?.id || !authToken) return;
    try {
      const result = await groupApi.clearGroupMessages({ token: authToken, groupId: group.id });
      if (result?.ok) {
        setShowClearMessagesConfirm(false);
        window.dispatchEvent(new CustomEvent('notesplus-show-toast', { detail: { type: 'success', title: t('groupInfoSidebar.clearedSuccessTitle'), message: t('groupInfoSidebar.clearedSuccessMessage') } }));
        window.dispatchEvent(new CustomEvent('notesplus-reload-group-messages', { detail: { groupId: group.id } }));
      }
    } catch {
      window.dispatchEvent(new CustomEvent('notesplus-show-toast', { detail: { type: 'error', title: t('common:errorTitle'), message: t('groupInfoSidebar.clearErrorMessage') } }));
    }
  };

  const handleDeleteMessages = async () => {
    if (!group?.id || !authToken) return;
    try {
      const result = await groupApi.deleteGroupMessages({ token: authToken, groupId: group.id });
      if (result?.ok) {
        setShowDeleteMessagesConfirm(false);
        if (selectGroup) selectGroup(null);
        onClose();
        window.dispatchEvent(new CustomEvent('notesplus-show-toast', { detail: { type: 'success', title: t('groupInfoSidebar.deletedSuccessTitle'), message: t('groupInfoSidebar.deletedSuccessMessage') } }));
      }
    } catch {
      window.dispatchEvent(new CustomEvent('notesplus-show-toast', { detail: { type: 'error', title: t('common:errorTitle'), message: t('groupInfoSidebar.deleteErrorMessage') } }));
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!group?.id || !memberId || !authToken) return;
    if (groupDetails?.isLocked || group?.isLocked) {
      setShowRemoveMemberConfirm(null);
      window.dispatchEvent(new CustomEvent('notesplus-show-toast', { detail: { type: 'error', title: t('groupInfoSidebar.groupLockedTitle'), message: t('groupInfoSidebar.groupLockedRemoveMemberMessage') } }));
      return;
    }
    try {
      const result = await groupApi.removeMember({ token: authToken, groupId: group.id, memberId });
      if (result?.ok) {
        await loadGroupDetails();
        setShowRemoveMemberConfirm(null);
      } else {
        window.dispatchEvent(new CustomEvent('notesplus-show-toast', { detail: { type: 'error', title: t('common:errorTitle'), message: result?.message || t('groupInfoSidebar.removeMemberErrorFallback') } }));
        setShowRemoveMemberConfirm(null);
      }
    } catch (error) {
      window.dispatchEvent(new CustomEvent('notesplus-show-toast', { detail: { type: 'error', title: t('common:errorTitle'), message: getApiErrorMessage(error, { fallback: t('groupInfoSidebar.removeMemberErrorFallback') }) } }));
      setShowRemoveMemberConfirm(null);
    }
  };

  const handleAddMember = async (friendEmail) => {
    if (!group?.id || !authToken || !friendEmail) return;
    if (groupDetails?.isLocked || group?.isLocked) {
      setAddMemberError(t('groupInfoSidebar.groupLockedAddMemberError'));
      return;
    }
    setAddingMember(true);
    setAddMemberError(null);
    try {
      const result = await groupApi.addMember({ token: authToken, groupId: group.id, memberEmail: friendEmail });
      if (result?.ok) {
        setShowAddMemberModal(false);
        await loadGroupDetails();
        window.dispatchEvent(new CustomEvent('notesplus-show-toast', { detail: { type: 'success', title: t('groupInfoSidebar.addMemberSuccessTitle'), message: t('groupInfoSidebar.addMemberSuccessMessage', { email: friendEmail }) } }));
      } else {
        setAddMemberError(result?.message || t('groupInfoSidebar.addMemberErrorFallback'));
      }
    } catch (error) {
      setAddMemberError(getApiErrorMessage(error, { fallback: t('groupInfoSidebar.addMemberErrorFallback') }));
    } finally {
      setAddingMember(false);
    }
  };

  const handleGroupAvatarSave = async (croppedFile) => {
    if (!authToken || !group?.id) return;
    setUploadingAvatar(true);
    try {
      const result = await avatarApi.uploadGroup({ token: authToken, groupId: group.id, file: croppedFile });
      if (!result?.ok) throw new Error(result?.message || t('groupInfoSidebar.avatarUploadFailedFallback'));
      await loadGroupDetails();
      window.dispatchEvent(new CustomEvent('notesplus-show-toast', { detail: { type: 'success', title: t('groupInfoSidebar.avatarUpdatedTitle') } }));
    } catch (error) {
      window.dispatchEvent(new CustomEvent('notesplus-show-toast', { detail: { type: 'error', title: getApiErrorMessage(error, { fallback: t('groupInfoSidebar.avatarUpdateFailedFallback') }) } }));
    } finally {
      setUploadingAvatar(false);
      if (avatarInputRef.current) avatarInputRef.current.value = '';
    }
  };

  const handleNameUpdate = async () => {
    if (!editingName?.trim() || !authToken || !group?.id) return;
    const trimmed = editingName.trim();
    if (trimmed === (groupDetails?.name || group.name)) { setIsEditingName(false); setEditingName(''); return; }
    setUpdatingName(true);
    try {
      const updated = await updateGroup(group.id, { name: trimmed });
      if (updated) {
        setGroupDetails(updated);
        setIsEditingName(false);
        setEditingName('');
        await loadGroupDetails();
        window.dispatchEvent(new CustomEvent('notesplus-show-toast', { detail: { type: 'success', title: t('common:success'), message: t('groupInfoSidebar.nameUpdatedMessage') } }));
      }
    } catch (error) {
      window.dispatchEvent(new CustomEvent('notesplus-show-toast', { detail: { type: 'error', title: t('common:errorTitle'), message: getApiErrorMessage(error, { fallback: t('groupInfoSidebar.nameUpdateErrorFallback') }) } }));
    } finally { setUpdatingName(false); }
  };

  const startEditingName = () => { setEditingName(groupDetails?.name || group.name); setIsEditingName(true); };
  const cancelEditingName = () => { setIsEditingName(false); setEditingName(''); };

  const openPhoto = async (url) => {
    if (!url) return;
    let resolved = url;
    if (!url.startsWith('http') && !url.startsWith('blob:') && !url.startsWith('data:')) {
      const apiBase = await getApiBaseUrl();
      resolved = apiBase.replace('/api', '') + url;
    }
    setSelectedImageUrl(resolved);
    setShowImageModal(true);
  };

  // Media/files from already-loaded messages
  const allGroupMsgs = useMemo(() => (groupMessages || {})[group?.id] || [], [groupMessages, group?.id]);
  const groupMediaImages = useMemo(() => allGroupMsgs.filter(m => m.type === 'image' && (m.fileUrl || m.file_url)), [allGroupMsgs]);
  const groupMediaFiles = useMemo(() => allGroupMsgs.filter(m => m.type === 'file' && (m.fileUrl || m.file_url)), [allGroupMsgs]);

  if (!isOpen || !group) return null;

  const currentUserRole = groupDetails?.userRole || group?.role;
  const isOwner = currentUserRole === 'owner';
  const isAdmin = currentUserRole === 'admin';
  const canManage = isOwner || isAdmin;
  const isDestroyed = groupDetails?.is_active === false || group?.is_active === false;
  const isLocked = groupDetails?.isLocked === true || group?.isLocked === true;
  const members = groupDetails?.members || [];
  const memberCount = members.length;
  const avatarUrl = groupDetails?.avatar_url || groupDetails?.avatarUrl || group?.avatar_url || group?.avatarUrl;
  const groupName = groupDetails?.name || group?.name || t('groupInfoSidebar.fallbackGroupName');
  const isMuted = group?.muted;
  const PREVIEW_COUNT = 5;
  const previewMembers = members.slice(0, PREVIEW_COUNT);
  const hasMoreMembers = members.length > PREVIEW_COUNT;

  const sidebarContent = (
    <>
      <AnimatePresence>
        {/* Main Panel */}
        {isOpen && (
          <motion.div
            key="group-info-sidebar"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 220 }}
            className="fixed right-0 top-0 h-full w-[300px] bg-[#0b1220] border-l border-white/[0.06] z-[10000] flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 h-[52px] border-b border-white/[0.05] flex-shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="w-5 h-5 rounded-full border border-[#3d8ef0]/60 flex items-center justify-center">
                  <span className="text-[10px] font-bold text-[#3d8ef0]">i</span>
                </div>
                <span className="text-sm font-semibold text-[#c8d8eb]">{t('friendInfoSidebar.detailsTitle')}</span>
              </div>
              <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-white/[0.05] text-[#4d6a8a] hover:text-white transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto">
              {/* Avatar + Name */}
              <div className="px-5 pt-6 pb-5">
                <div className="flex items-start gap-4">
                  {/* Avatar */}
                  <div className="relative flex-shrink-0">
                    <button
                      className="relative block focus:outline-none group"
                      onClick={() => openPhoto(avatarUrl)}
                    >
                      <div className="w-[60px] h-[60px] rounded-full overflow-hidden ring-2 ring-white/[0.07]">
                        <GroupAvatar group={groupDetails || group} size="full" />
                      </div>
                      {avatarUrl && (
                        <div className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                          <ZoomIn className="w-4 h-4 text-white" />
                        </div>
                      )}
                    </button>
                    {/* Online dot */}
                    <div className="absolute bottom-0.5 right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-[#0b1220]" />
                    {/* Camera */}
                    {canManage && (
                      <>
                        <input
                          ref={avatarInputRef}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            e.target.value = '';
                            if (!file) return;
                            if (file.size > 15 * 1024 * 1024) {
                              window.dispatchEvent(new CustomEvent('notesplus-show-toast', { detail: { type: 'error', title: t('groupInfoSidebar.fileTooLarge') } }));
                              return;
                            }
                            setSelectedGroupImageFile(file);
                            setShowGroupCropModal(true);
                          }}
                        />
                        <motion.button
                          whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                          onClick={() => avatarInputRef.current?.click()}
                          disabled={uploadingAvatar}
                          className="absolute -bottom-1 -right-1 w-6 h-6 bg-[#3d8ef0] rounded-full flex items-center justify-center text-white border-2 border-[#0b1220] shadow disabled:opacity-50 transition-colors"
                        >
                          {uploadingAvatar ? <Loader2 className="w-3 h-3 animate-spin" /> : <Camera className="w-3 h-3" />}
                        </motion.button>
                      </>
                    )}
                  </div>

                  {/* Name & info */}
                  <div className="flex-1 min-w-0 pt-1">
                    {isEditingName ? (
                      <div className="flex items-center gap-1.5">
                        <input
                          type="text"
                          value={editingName}
                          onChange={e => setEditingName(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') handleNameUpdate(); else if (e.key === 'Escape') cancelEditingName(); }}
                          disabled={updatingName}
                          className="flex-1 px-2 py-1 bg-[#1e2d47] border border-[#3d8ef0]/40 rounded-lg text-sm font-bold text-white focus:outline-none focus:border-[#3d8ef0] min-w-0"
                          autoFocus
                        />
                        <button onClick={handleNameUpdate} disabled={updatingName || !editingName.trim()} className="w-6 h-6 rounded bg-[#3d8ef0] flex items-center justify-center text-white disabled:opacity-40">
                          {updatingName ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                        </button>
                        <button onClick={cancelEditingName} className="w-6 h-6 rounded bg-[#1e2d47] flex items-center justify-center text-[#8aaccc]">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <h2 className="text-[17px] font-bold text-white leading-tight truncate">{groupName}</h2>
                        {canManage && (
                          <button onClick={startEditingName} className="flex-shrink-0 text-[#4d6a8a] hover:text-[#8aaccc] transition-colors">
                            <Edit2 className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    )}
                    <p className="text-[12px] text-[#4d6a8a] mt-0.5">{t('groupInfoSidebar.membersActiveSuffix', { count: memberCount })}</p>
                  </div>
                </div>

              </div>

              {/* Quick Actions */}
              <div className="px-5 pb-5">
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { icon: Search, label: t('groupInfoSidebar.quickActions.search'), onClick: () => {} },
                    { icon: isMuted ? BellOff : Bell, label: isMuted ? t('groupInfoSidebar.quickActions.muteOn') : t('groupInfoSidebar.quickActions.muteOff'), onClick: handleToggleMute, active: isMuted },
                    { icon: UserPlus, label: t('groupInfoSidebar.quickActions.addMember'), onClick: () => { if (isLocked) { window.dispatchEvent(new CustomEvent('notesplus-show-toast', { detail: { type: 'error', title: t('groupInfoSidebar.groupLockedTitle'), message: t('groupInfoSidebar.groupLockedAddMemberMessage') } })); return; } if (!canManage) return; if (reloadFriends) reloadFriends(); setShowAddMemberModal(true); }, disabled: !canManage || isLocked },
                    { icon: Star, label: t('groupInfoSidebar.quickActions.starredMessages'), onClick: () => { setShowStarredMessages(true); loadStarredMessages(); } },
                  ].map(action => (
                    <button
                      key={action.label}
                      onClick={action.onClick}
                      disabled={action.disabled}
                      className={`flex flex-col items-center gap-2 py-3.5 px-1 rounded-xl transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${action.active ? 'bg-[#1e2d47]' : 'bg-[#161f31] hover:bg-[#1a2640]'}`}
                    >
                      <action.icon className={`w-[18px] h-[18px] ${action.active ? 'text-amber-400' : 'text-[#3d8ef0]'}`} />
                      <span className="text-[9px] font-medium text-[#8aaccc] text-center leading-tight">{action.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* GRUP HAKKINDA */}
              <div className="px-5 pb-5">
                <p className="text-[10px] font-semibold text-[#4d6a8a] uppercase tracking-widest mb-3">{t('groupInfoSidebar.aboutGroupLabel')}</p>
                <div className="grid grid-cols-3 gap-2.5">
                  {[
                    { value: memberCount, label: t('groupInfoSidebar.stats.members') },
                    { value: groupMediaImages.length, label: t('groupInfoSidebar.stats.media') },
                    { value: groupMediaFiles.length, label: t('groupInfoSidebar.stats.files') },
                  ].map(stat => (
                    <div key={stat.label} className="bg-[#161f31] rounded-xl p-4 text-center">
                      <p className="text-[16px] font-bold text-white leading-none">{stat.value}</p>
                      <p className="text-[10px] text-[#4d6a8a] mt-1.5">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Divider */}
              <div className="mx-5 h-px bg-white/[0.05] mb-5" />

              {/* ÜYELER */}
              <div className="px-5 pb-5">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[10px] font-semibold text-[#4d6a8a] uppercase tracking-widest">{t('groupInfoSidebar.membersLabel')}</p>
                  {hasMoreMembers && (
                    <button onClick={() => setShowAllMembers(true)} className="text-[11px] text-[#3d8ef0] hover:text-[#5ca3f5] transition-colors">
                      {t('groupInfoSidebar.seeAll')}
                    </button>
                  )}
                </div>
                <div className="space-y-1">
                  {previewMembers.map(member => {
                    const isCurrentUser = member.id === user?.id;
                    const canRemove = canManage && !isCurrentUser && member.role !== 'owner' && !isLocked;
                    const roleLabel = member.role === 'owner' ? t('groupInfoSidebar.roleOwner') : member.role === 'admin' ? t('groupInfoSidebar.roleAdmin') : null;
                    const roleBg = member.role === 'owner' ? 'bg-amber-400/[0.12] text-amber-400' : 'bg-[#3d8ef0]/[0.12] text-[#3d8ef0]';
                    return (
                      <div
                        key={member.id}
                        className="flex items-center gap-3 px-2 py-2.5 rounded-xl hover:bg-[#161f31] transition-colors cursor-default"
                        onContextMenu={e => { e.preventDefault(); e.stopPropagation(); setMemberContextMenu({ isOpen: true, x: e.clientX, y: e.clientY, member }); }}
                      >
                        <div className="relative flex-shrink-0">
                          <div className="w-9 h-9 rounded-full bg-[#1e2d47] flex items-center justify-center text-[11px] font-semibold text-[#3d8ef0] overflow-hidden">
                            {member.avatarUrl ? <img src={member.avatarUrl} alt={getDisplayName(member)} className="w-full h-full object-cover" /> : getInitials(getDisplayName(member))}
                          </div>
                          <div className="absolute bottom-0 right-0 w-2 h-2 bg-green-500 rounded-full border border-[#0b1220]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium text-white truncate block leading-tight">
                            <Username user={member} variant="default" plain />
                          </span>
                        </div>
                        {roleLabel && (
                          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${roleBg} flex-shrink-0`}>{roleLabel}</span>
                        )}
                        {canRemove && !roleLabel && (
                          <button onClick={() => setShowRemoveMemberConfirm(member)} className="w-6 h-6 rounded flex items-center justify-center text-[#4d6a8a] hover:text-red-400 hover:bg-red-400/[0.08] transition-colors flex-shrink-0">
                            <UserMinus className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Divider */}
              <div className="mx-5 h-px bg-white/[0.05] mb-5" />

              {/* MEDYA */}
              <div className="px-5 pb-5">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[10px] font-semibold text-[#4d6a8a] uppercase tracking-widest">{t('friendInfoSidebar.mediaLabel')}</p>
                  {groupMediaImages.length > 0 && (
                    <span className="text-[11px] text-[#4d6a8a]">{t('friendInfoSidebar.itemCount', { count: groupMediaImages.length })}</span>
                  )}
                </div>
                {groupMediaImages.length > 0 ? (
                  <div className="grid grid-cols-3 gap-1.5">
                    {groupMediaImages.slice(0, 3).map(img => {
                      const url = img.fileUrl || img.file_url;
                      return (
                        <motion.div
                          key={img.id}
                          whileHover={{ scale: 1.04 }}
                          className="aspect-square rounded-lg overflow-hidden cursor-pointer bg-[#161f31]"
                          onClick={() => openPhoto(url)}
                        >
                          <img src={url} alt={t('friendInfoSidebar.mediaAlt')} className="w-full h-full object-cover" onError={e => { e.currentTarget.parentElement.style.display = 'none'; }} />
                        </motion.div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-xs text-[#3d5470]">{t('groupInfoSidebar.noMediaYet')}</p>
                )}
              </div>

              {/* Divider */}
              <div className="mx-5 h-px bg-white/[0.05] mb-5" />

              {/* Bottom Actions (scrollable part) */}
              <div className="px-5 pb-8 space-y-2.5">
                <button
                  onClick={() => setShowClearMessagesConfirm(true)}
                  className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl bg-[#161f31] hover:bg-[#1a2640] transition-colors"
                >
                  <Trash2 className="w-4 h-4 text-[#4d6a8a] flex-shrink-0" />
                  <span className="text-sm text-[#8aaccc]">{t('friendInfoSidebar.clearChat')}</span>
                </button>
                {isOwner ? (
                  !isDestroyed ? (
                    <button onClick={() => setShowDestroyConfirm(true)} className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl bg-[#161f31] hover:bg-red-500/[0.08] transition-colors">
                      <LogOut className="w-4 h-4 text-red-400 flex-shrink-0" />
                      <span className="text-sm text-red-400">{t('groupInfoSidebar.destroyGroup')}</span>
                    </button>
                  ) : (
                    <button onClick={() => setShowDeleteMessagesConfirm(true)} className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl bg-[#161f31] hover:bg-red-500/[0.08] transition-colors">
                      <Trash2 className="w-4 h-4 text-red-400 flex-shrink-0" />
                      <span className="text-sm text-red-400">{t('groupInfoSidebar.deleteMessagesAction')}</span>
                    </button>
                  )
                ) : (
                  <button onClick={() => setShowLeaveConfirm(true)} className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl bg-[#161f31] hover:bg-red-500/[0.08] transition-colors">
                    <LogOut className="w-4 h-4 text-red-400 flex-shrink-0" />
                    <span className="text-sm text-red-400">{t('groupInfoSidebar.leaveGroup')}</span>
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ---- All modals OUTSIDE motion.div to avoid transform stacking context ---- */}
      <AvatarCropModal
        isOpen={showGroupCropModal}
        onClose={() => setShowGroupCropModal(false)}
        imageFile={selectedGroupImageFile}
        onSave={handleGroupAvatarSave}
        title={t('groupInfoSidebar.setGroupPhotoTitle')}
      />

      {/* Photo modal - centered in viewport, not constrained by sidebar transforms */}
      <ImageModal
        isOpen={showImageModal}
        onClose={() => { setShowImageModal(false); setSelectedImageUrl(null); }}
        imageUrl={selectedImageUrl}
      />

      <DeleteModal isOpen={showLeaveConfirm} onClose={() => setShowLeaveConfirm(false)} onConfirm={handleLeaveGroup} title={t('groupInfoSidebar.leaveGroup')} message={t('groupInfoSidebar.leaveConfirmMessage', { name: group.name })} />
      <DeleteModal isOpen={showDestroyConfirm} onClose={() => setShowDestroyConfirm(false)} onConfirm={handleDestroyGroup} title={t('groupInfoSidebar.destroyGroup')} message={t('groupInfoSidebar.destroyConfirmMessage', { name: group.name })} />
      <DeleteModal isOpen={showClearMessagesConfirm} onClose={() => setShowClearMessagesConfirm(false)} onConfirm={handleClearMessages} title={t('friendInfoSidebar.clearChat')} message={t('groupInfoSidebar.clearConfirmMessage', { name: group.name })} />
      <DeleteModal isOpen={showDeleteMessagesConfirm} onClose={() => setShowDeleteMessagesConfirm(false)} onConfirm={handleDeleteMessages} title={t('groupInfoSidebar.deleteMessagesAction')} message={t('groupInfoSidebar.deleteMessagesConfirmMessage', { name: group.name })} />
      {showRemoveMemberConfirm && (
        <DeleteModal isOpen={!!showRemoveMemberConfirm} onClose={() => setShowRemoveMemberConfirm(null)} onConfirm={() => handleRemoveMember(showRemoveMemberConfirm.id)} title={t('groupInfoSidebar.removeMemberTitle')} message={t('groupInfoSidebar.removeMemberConfirmMessage', { name: getDisplayName(showRemoveMemberConfirm) })} />
      )}

      {/* Add Member Modal */}
      {showAddMemberModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100002]" onClick={() => { setShowAddMemberModal(false); setAddMemberError(null); }}>
          <div className="fixed inset-0 flex items-center justify-center z-[100011] p-4 pointer-events-none" onClick={e => e.stopPropagation()}>
            <div className="bg-[#0d1424] border border-white/[0.08] rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col pointer-events-auto">
              <div className="flex items-center justify-between px-4 py-4 border-b border-white/[0.06]">
                <h3 className="text-sm font-semibold text-white">{t('groupInfoSidebar.selectFriendTitle')}</h3>
                <button onClick={() => { setShowAddMemberModal(false); setAddMemberError(null); }} className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-white/[0.05] text-[#4d6a8a] hover:text-white transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                {(() => {
                  const available = friends?.filter(f => !f.isDeleted && !groupDetails?.members?.some(m => (m.email || m.id) === (f.email || f.id))) || [];
                  if (!friends?.length) return <div className="text-center text-[#4d6a8a] py-8 text-sm">{t('groupInfoSidebar.noFriendsYet')}</div>;
                  if (!available.length) return <div className="text-center text-[#4d6a8a] py-8 text-sm">{t('groupInfoSidebar.allFriendsInGroup')}</div>;
                  return (
                    <div className="space-y-1">
                      {available.map(f => {
                        const fid = f.email || f.id;
                        return (
                          <button key={fid} onClick={() => handleAddMember(f.email || fid)} disabled={addingMember} className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-[#161f31] transition-colors text-left">
                            <div className="w-9 h-9 rounded-full bg-[#1e2d47] flex items-center justify-center text-xs font-semibold text-[#3d8ef0] flex-shrink-0 overflow-hidden">
                              {f.avatarUrl ? <img src={f.avatarUrl} alt={getDisplayName(f)} className="w-full h-full object-cover" /> : getInitials(getDisplayName(f))}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-white truncate"><Username user={f} variant="default" /></p>
                              {f.email && <p className="text-xs text-[#4d6a8a] truncate">{f.email}</p>}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
              {addMemberError && <div className="px-4 pb-4"><p className="text-sm text-red-400">{addMemberError}</p></div>}
            </div>
          </div>
        </div>
      )}

      {/* Starred Messages Modal */}
      {showStarredMessages && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100010]" onClick={() => setShowStarredMessages(false)}>
          <div className="fixed inset-0 flex items-center justify-center z-[100011] p-4 pointer-events-none" onClick={e => e.stopPropagation()}>
            <div className="bg-[#0d1424] border border-white/[0.08] rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col pointer-events-auto">
              <div className="flex items-center justify-between px-4 py-4 border-b border-white/[0.06]">
                <div className="flex items-center gap-2"><Star className="w-4 h-4 text-amber-400" /><h3 className="text-sm font-semibold text-white">{t('groupInfoSidebar.starredMessagesTitle')}</h3></div>
                <button onClick={() => setShowStarredMessages(false)} className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-white/[0.05] text-[#4d6a8a] hover:text-white transition-colors"><X className="w-4 h-4" /></button>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                {starredMessages.length > 0 ? (
                  <div className="space-y-1.5">
                    {starredMessages.map(message => (
                      <div key={message.id} className="p-3 rounded-xl bg-[#161f31] hover:bg-[#1e2d47] transition-colors cursor-pointer" onClick={() => { window.dispatchEvent(new CustomEvent('notesplus-scroll-to-message', { detail: { groupId: group.id, messageId: message.id } })); setShowStarredMessages(false); }}>
                        <div className="flex items-start gap-3">
                          <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400 flex-shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-white line-clamp-2">{message.type === 'text' ? message.content?.replace(/<[^>]*>/g, '').substring(0, 100) : message.file_name || t('forwardModal.typeFallback.file')}</p>
                            <p className="text-[10px] text-[#4d6a8a] mt-1">{new Date(message.created_at || message.timestamp).toLocaleString(dateLocaleTag)}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Star className="w-10 h-10 text-[#1e2d47] mx-auto mb-3" />
                    <p className="text-sm text-[#4d6a8a]">{t('groupInfoSidebar.noStarredYet')}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* All Members Modal */}
      {showAllMembers && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100010]" onClick={() => setShowAllMembers(false)}>
          <div className="fixed inset-0 flex items-center justify-center z-[100011] p-4 pointer-events-none" onClick={e => e.stopPropagation()}>
            <div className="bg-[#0d1424] border border-white/[0.08] rounded-2xl shadow-2xl w-full max-w-sm max-h-[75vh] overflow-hidden flex flex-col pointer-events-auto">
              <div className="flex items-center justify-between px-4 py-4 border-b border-white/[0.06]">
                <div><h3 className="text-sm font-semibold text-white">{groupName}</h3><p className="text-[10px] text-[#4d6a8a] mt-0.5">{t('groupInfoSidebar.memberCountLabel', { count: memberCount })}</p></div>
                <button onClick={() => setShowAllMembers(false)} className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-white/[0.05] text-[#4d6a8a] hover:text-white transition-colors"><X className="w-4 h-4" /></button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-0.5">
                {members.map(member => {
                  const isCurrentUser = member.id === user?.id;
                  const canRemove = canManage && !isCurrentUser && member.role !== 'owner' && !isLocked;
                  const roleLabel = member.role === 'owner' ? t('groupInfoSidebar.roleOwner') : member.role === 'admin' ? t('groupInfoSidebar.roleAdmin') : null;
                  const roleBg = member.role === 'owner' ? 'bg-amber-400/[0.12] text-amber-400' : 'bg-[#3d8ef0]/[0.12] text-[#3d8ef0]';
                  return (
                    <div key={member.id} className="flex items-center gap-2.5 px-2 py-2 rounded-xl hover:bg-[#161f31] transition-colors" onContextMenu={e => { e.preventDefault(); e.stopPropagation(); setMemberContextMenu({ isOpen: true, x: e.clientX, y: e.clientY, member }); setShowAllMembers(false); }}>
                      <div className="w-8 h-8 rounded-full bg-[#1e2d47] flex items-center justify-center text-[11px] font-semibold text-[#3d8ef0] flex-shrink-0 overflow-hidden">
                        {member.avatarUrl ? <img src={member.avatarUrl} alt={getDisplayName(member)} className="w-full h-full object-cover" /> : getInitials(getDisplayName(member))}
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium text-white truncate block"><Username user={member} variant="default" plain /></span>
                        {isCurrentUser && <span className="text-[10px] text-[#4d6a8a]">{t('groupInfoSidebar.youLabel')}</span>}
                      </div>
                      {roleLabel && <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${roleBg} flex-shrink-0`}>{roleLabel}</span>}
                      {canRemove && (
                        <button onClick={() => { setShowRemoveMemberConfirm(member); setShowAllMembers(false); }} className="w-6 h-6 rounded flex items-center justify-center text-[#4d6a8a] hover:text-red-400 hover:bg-red-400/[0.08] transition-colors flex-shrink-0"><UserMinus className="w-3 h-3" /></button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Member Context Menu */}
      <ContextMenu
        isOpen={memberContextMenu.isOpen && !!memberContextMenu.member}
        position={{ x: memberContextMenu.x, y: memberContextMenu.y }}
        items={(() => {
          const member = memberContextMenu.member;
          if (!member) return [];
          const isCurrentUser = member.id === user?.id;
          const isFriend = friends?.some(f => !f.isDeleted && (f.email || f.id) === member.email);
          const canKick = !isCurrentUser && !isLocked && member.role !== 'owner' && ((currentUserRole === 'owner') || (currentUserRole === 'admin' && member.role === 'member'));
          const canPromote = currentUserRole === 'owner' && member.role === 'member' && !isCurrentUser;
          const canDemote = currentUserRole === 'owner' && member.role === 'admin' && !isCurrentUser;
          return [
            !isCurrentUser && isFriend && { label: t('groupInfoSidebar.sendPrivateMessage'), icon: MessageCircle, onClick: () => { const fid = member.email || member.id; if (fid && selectFriend) selectFriend(fid); } },
            !isCurrentUser && !isFriend && member.email && { label: t('groupInfoSidebar.addFriendAction'), icon: UserPlus, onClick: async () => { try { await chatApi.sendFriendRequest({ token: authToken, receiverEmail: member.email }); window.dispatchEvent(new CustomEvent('notesplus-show-toast', { detail: { type: 'success', title: t('groupInfoSidebar.friendInviteSentTitle'), message: t('groupInfoSidebar.friendInviteSentMessage', { email: member.email }) } })); } catch (err) { window.dispatchEvent(new CustomEvent('notesplus-show-toast', { detail: { type: 'error', title: t('common:errorTitle'), message: getApiErrorMessage(err, { fallback: t('groupInfoSidebar.inviteSendErrorFallback') }) } })); } } },
            canPromote && { label: t('groupInfoSidebar.makeAdmin'), icon: Shield, onClick: async () => { try { await groupApi.promoteMember({ token: authToken, groupId: group.id, memberId: member.id }); await loadGroupDetails(); } catch (err) { window.dispatchEvent(new CustomEvent('notesplus-show-toast', { detail: { type: 'error', title: t('common:errorTitle'), message: getApiErrorMessage(err, { fallback: t('groupInfoSidebar.makeAdminErrorFallback') }) } })); } } },
            canDemote && { label: t('groupInfoSidebar.makeMember'), icon: Users, onClick: async () => { try { await groupApi.demoteMember({ token: authToken, groupId: group.id, memberId: member.id }); await loadGroupDetails(); } catch (err) { window.dispatchEvent(new CustomEvent('notesplus-show-toast', { detail: { type: 'error', title: t('common:errorTitle'), message: getApiErrorMessage(err, { fallback: t('groupInfoSidebar.makeMemberErrorFallback') }) } })); } } },
            canKick && { label: t('groupInfoSidebar.removeFromGroup'), icon: UserMinus, danger: true, onClick: () => setShowRemoveMemberConfirm(member) },
          ];
        })()}
        onClose={() => setMemberContextMenu({ isOpen: false, x: 0, y: 0, member: null })}
      />
    </>
  );

  if (typeof document !== 'undefined') {
    return createPortal(sidebarContent, document.body);
  }
  return sidebarContent;
};

export default GroupInfoSidebar;
